import React, { useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  CloudUpload,
  Delete,
  Edit,
  Image as ImageIcon,
} from "@mui/icons-material";

interface LogoPickerProps {
  logo?: string | null;
  label?: string;
  deleteLogo: (e: React.MouseEvent) => void;
  base64: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const LogoPicker: React.FC<LogoPickerProps> = ({
  logo,
  label = "Logo",
  deleteLogo,
  base64,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ width: "431px", maxWidth: "431px", minWidth: "431px" }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={base64}
        style={{ display: "none" }}
      />

      <Box
        sx={{
          backgroundColor: "#fff",
          border: "2px solid #e5e7eb",
          borderRadius: "12px",
          padding: "24px",
          transition: "border-color 0.2s ease",
          "&:hover": {
            borderColor: "#d1d5db",
          },
        }}
      >
        {logo ? (
          /* Logo Preview State */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Preview Container */}
            <Box
              sx={{
                position: "relative",
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "120px",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt={label}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "112px",
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                onClick={handleUploadClick}
                startIcon={<Edit />}
                variant="outlined"
                sx={{
                  flex: 1,
                  color: "#374151",
                  borderColor: "#d1d5db",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#9ca3af",
                  },
                }}
              >
                Change Logo
              </Button>
              <Button
                onClick={deleteLogo}
                startIcon={<Delete />}
                variant="outlined"
                sx={{
                  flex: 1,
                  color: "#dc2626",
                  borderColor: "#fca5a5",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#fef2f2",
                    borderColor: "#f87171",
                  },
                }}
              >
                Remove
              </Button>
            </Box>
          </Box>
        ) : (
          /* Upload State */
          <Box sx={{ textAlign: "center" }}>
            {/* Upload Area */}
            <Box
              onClick={handleUploadClick}
              sx={{
                cursor: "pointer",
                border: "2px dashed #d1d5db",
                borderRadius: "8px",
                padding: "32px",
                mb: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#3b82f6",
                  backgroundColor: "#eff6ff",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ImageIcon sx={{ fontSize: "32px", color: "#9ca3af" }} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#4b5563",
                    fontWeight: 500,
                  }}
                >
                  Drop your logo here or click to browse
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    fontSize: "0.875rem",
                  }}
                >
                  Image up to 5MB
                </Typography>
              </Box>
            </Box>

            {/* Upload Button */}
            <Button
              onClick={handleUploadClick}
              startIcon={<CloudUpload />}
              variant="contained"
              sx={{
                backgroundColor: "#2563eb",
                textTransform: "none",
                px: 3,
                py: 1.5,
                "&:hover": {
                  backgroundColor: "#1d4ed8",
                },
              }}
            >
              Upload {label}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LogoPicker;
